/**
 * Occupancy grid + placement law (doc 03 §Placement rules). Derived from map +
 * structures — rebuilt after a snapshot restore, never serialized.
 */

import type { MapDef, Structure } from './types.ts';

export const T_FREE = 0;
export const T_ROCK = 1;
export const T_VEIN = 2;
export const T_VENT = 3;

export class SimGrid {
  readonly size: number;
  /** Terrain per tile: T_FREE / T_ROCK / T_VEIN / T_VENT. */
  readonly terrain: Uint8Array;
  /** Structure id per tile, or -1. Ruins keep their id here (footprint reserved). */
  readonly occ: Int32Array;
  /** True where building is banned by spawn-edge margin (doc 03). */
  readonly spawnBan: Uint8Array;

  constructor(map: MapDef) {
    const n = map.size;
    this.size = n;
    this.terrain = new Uint8Array(n * n);
    this.occ = new Int32Array(n * n).fill(-1);
    this.spawnBan = new Uint8Array(n * n);

    for (const r of map.rocks) {
      for (let i = r.i0; i <= r.i1; i++) {
        for (let j = r.j0; j <= r.j1; j++) this.terrain[this.idx(i, j)] = T_ROCK;
      }
    }
    for (const [i, j] of map.oreVeins) this.terrain[this.idx(i!, j!)] = T_VEIN;
    for (const [i, j] of map.gasVents) this.terrain[this.idx(i!, j!)] = T_VENT;

    const m = map.spawnMargin;
    for (const edge of map.spawnEdges) {
      for (let a = 0; a < n; a++) {
        for (let d = 0; d <= m; d++) {
          if (edge === 'n') this.spawnBan[this.idx(a, d)] = 1;
          if (edge === 's') this.spawnBan[this.idx(a, n - 1 - d)] = 1;
          if (edge === 'w') this.spawnBan[this.idx(d, a)] = 1;
          if (edge === 'e') this.spawnBan[this.idx(n - 1 - d, a)] = 1;
        }
      }
    }
  }

  idx(i: number, j: number): number {
    return j * this.size + i;
  }

  inBounds(i: number, j: number): boolean {
    return i >= 0 && j >= 0 && i < this.size && j < this.size;
  }

  /** Rebuild occupancy from the structure list (after restore or destruction). */
  sync(structures: Structure[]): void {
    this.occ.fill(-1);
    for (const s of structures) {
      for (let di = 0; di < s.size; di++) {
        for (let dj = 0; dj < s.size; dj++) this.occ[this.idx(s.i + di, s.j + dj)] = s.id;
      }
    }
  }

  /**
   * Placement legality for a `size`-footprint at anchor (i, j). Returns null if
   * legal, or a human-readable reason (which the UI shows verbatim).
   * `ruinId` reports when the ONLY blocker is a single matching ruin — the
   * rebuild-from-ruin path (doc 03: 50% cost). `depositTiles` lists the deposit
   * tiles the footprint covers (extractors bind to them).
   *
   * `wants` is the deposit kind this def extracts (doc 03/04: no building on
   * deposits except their extractor, and an extractor NEEDS its deposit).
   */
  placeCheck(
    i: number,
    j: number,
    size: number,
    structures: Structure[],
    wants: 'vein' | 'vent' | null = null,
  ): { ok: boolean; reason: string; ruinId: number; depositTiles: number[] } {
    let ruinId = -1;
    const depositTiles: number[] = [];
    const fail = (reason: string): { ok: boolean; reason: string; ruinId: number; depositTiles: number[] } => ({
      ok: false,
      reason,
      ruinId: -1,
      depositTiles: [],
    });
    for (let di = 0; di < size; di++) {
      for (let dj = 0; dj < size; dj++) {
        const ti = i + di;
        const tj = j + dj;
        if (!this.inBounds(ti, tj)) return fail('OUT OF BOUNDS');
        const t = this.terrain[this.idx(ti, tj)]!;
        if (t === T_ROCK) return fail('SOLID ROCK');
        if (t === T_VEIN) {
          if (wants !== 'vein') return fail('ORE VEIN — ORE RIGS ONLY');
          depositTiles.push(this.idx(ti, tj));
        }
        if (t === T_VENT) {
          if (wants !== 'vent') return fail('GAS VENT — GAS TAPS ONLY');
          depositTiles.push(this.idx(ti, tj));
        }
        if (this.spawnBan[this.idx(ti, tj)]) {
          return fail('TOO CLOSE TO A SPAWN EDGE');
        }
        const occ = this.occ[this.idx(ti, tj)]!;
        if (occ >= 0) {
          const s = structures.find((x) => x.id === occ)!;
          if (!s.ruin) return fail('OCCUPIED');
          if (ruinId !== -1 && ruinId !== occ) return fail('OCCUPIED');
          ruinId = occ;
        }
      }
    }
    if (wants === 'vein' && depositTiles.length === 0) return fail('NEEDS AN ORE VEIN');
    if (wants === 'vent' && depositTiles.length === 0) return fail('NEEDS A GAS VENT');
    if (ruinId !== -1) {
      const ruin = structures.find((x) => x.id === ruinId)!;
      // rebuild only fits the exact footprint it is standing on
      if (ruin.i !== i || ruin.j !== j || ruin.size !== size) {
        return fail('A RUIN HOLDS THIS FOOTPRINT');
      }
    }
    return { ok: true, reason: '', ruinId, depositTiles };
  }
}
